import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Paperclip, X, Send, Check, Loader2, FileText } from 'lucide-react';
import { useLang } from '../../i18n/LanguageContext';
import api from '../../api';

const PROJECT_TYPES = ['manufacturer', 'custom', 'product', 'equipment', 'partner', 'other'];
const FREQUENCIES = ['once', 'monthly', 'quarterly', 'yearly', 'tbd'];
const CURRENCIES = ['CAD', 'USD', 'EUR', 'CNY'];
const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'txt', 'ppt', 'pptx', 'zip', 'dwg', 'step', 'stp'];
const MAX_FILES = 8;
const MAX_MB = 15;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

// Load reCAPTCHA v3 script once, lazily.
function loadRecaptcha() {
  if (!RECAPTCHA_SITE_KEY || typeof window === 'undefined') return;
  if (window.__yuvilleRecaptchaLoaded) return;
  window.__yuvilleRecaptchaLoaded = true;
  const s = document.createElement('script');
  s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

async function getRecaptchaToken() {
  if (!RECAPTCHA_SITE_KEY || typeof window === 'undefined' || !window.grecaptcha) return '';
  return new Promise((resolve) => {
    // Timeout safety — don't block the form if reCAPTCHA misbehaves.
    const timer = setTimeout(() => resolve(''), 6000);
    try {
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'rfq_submit' });
          clearTimeout(timer);
          resolve(token || '');
        } catch (e) {
          console.warn('[reCAPTCHA] execute failed — submitting without token', e);
          clearTimeout(timer);
          resolve('');
        }
      });
    } catch (e) {
      clearTimeout(timer);
      resolve('');
    }
  });
}

const emptyForm = {
  project_type: 'manufacturer', project_type_other: '',
  product: '', description: '',
  quantity_value: '', quantity_unit: '',
  frequency: 'once', budget_amount: '', budget_currency: 'CAD',
  market: '', standards: '',
  customization_required: 'no', customization_details: '',
  timeline: '', reference_url: '',
  contact_name: '', contact_company: '', contact_role: '', contact_country: '',
  contact_email: '', contact_phone: '', contact_website: '',
  hp: '',
};

// Field wrappers — shared styles reuse the existing palette.
const label = 'block text-sm font-semibold text-brand-navy mb-1.5';
const req = <span className="text-brand-yellow ml-0.5">*</span>;
const input = 'w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 outline-none text-sm bg-white transition';
const textarea = input + ' min-h-[110px] resize-y';
const chip = 'flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm cursor-pointer transition select-none';

export default function RfqDialog({ open, onOpenChange }) {
  const { t } = useLang();
  const r = t('platform.rfq');
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  // Lazy-load reCAPTCHA script when the dialog first opens.
  useEffect(() => { if (open) loadRecaptcha(); }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = (list) => {
    setErr('');
    const cur = [...files];
    for (const f of Array.from(list)) {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) { setErr(`${r.errFile} ${f.name}`); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { setErr(`${r.errFile} ${f.name} (>${MAX_MB} MB)`); continue; }
      if (cur.length >= MAX_FILES) break;
      cur.push(f);
    }
    setFiles(cur);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const reset = () => { setForm(emptyForm); setFiles([]); setOk(false); setErr(''); };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.contact_name.trim() || !form.contact_email.trim()) { setErr(r.errBody); return; }
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) { setErr(r.errBody); return; }

    setBusy(true);
    try {
      const token = await getRecaptchaToken();
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (token) fd.append('recaptcha_token', token);
      files.forEach((f) => fd.append('files', f));
      await api.post('/rfq', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOk(true);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : r.errBody);
    } finally { setBusy(false); }
  };

  const close = () => { onOpenChange(false); setTimeout(reset, 250); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="rfq-dialog">
        {ok ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-brand-yellow/20 border-2 border-brand-yellow flex items-center justify-center mx-auto mb-5">
              <Check className="w-8 h-8 text-brand-navy" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-brand-navy mb-3">{r.okTitle}</h2>
            <p className="text-gray-600 leading-relaxed max-w-lg mx-auto mb-8">{r.okBody}</p>
            <button data-testid="rfq-close-success" onClick={close} className="inline-flex items-center gap-2 px-6 py-3 bg-brand-yellow text-brand-navy font-bold rounded-full text-sm hover:bg-yellow-400 transition">
              {r.okClose}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <div className="sticky top-0 bg-brand-navy text-white px-6 py-5 md:px-8 md:py-6 z-10">
              <DialogHeader>
                <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-brand-yellow/20 border border-brand-yellow text-brand-yellow text-[11px] font-bold tracking-widest uppercase w-fit">
                  01 · {t('platform.partnersSteps')[0].t}
                </div>
                <DialogTitle className="text-2xl md:text-3xl font-bold text-white leading-tight">{r.dialogTitle}</DialogTitle>
                <DialogDescription className="text-white/80 mt-2 leading-relaxed">{r.intro}</DialogDescription>
              </DialogHeader>
            </div>

            <div className="px-6 md:px-8 py-6 space-y-10">
              {/* Section 1 — Project */}
              <section>
                <h3 className="text-lg font-bold text-brand-navy mb-1">{r.s1}</h3>
                <p className="text-sm text-gray-500 mb-4">{r.s1Sub}</p>
                <div className="flex flex-wrap gap-2">
                  {PROJECT_TYPES.map((k) => {
                    const active = form.project_type === k;
                    return (
                      <label key={k} className={`${chip} ${active ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-brand-navy border-gray-300 hover:border-brand-yellow'}`}>
                        <input type="radio" name="project_type" value={k} checked={active} onChange={() => set('project_type', k)} className="sr-only" />
                        {r.pt[k]}
                      </label>
                    );
                  })}
                </div>
                {form.project_type === 'other' && (
                  <div className="mt-4">
                    <label className={label}>{r.ptOtherLabel}{req}</label>
                    <input type="text" required value={form.project_type_other} onChange={(e) => set('project_type_other', e.target.value)} className={input} data-testid="rfq-project-other" />
                  </div>
                )}
              </section>

              {/* Section 2 — Need */}
              <section>
                <h3 className="text-lg font-bold text-brand-navy mb-4">{r.s2}</h3>
                <div className="space-y-4">
                  <div>
                    <label className={label}>{r.f.product}</label>
                    <input type="text" value={form.product} placeholder={r.f.productPh} onChange={(e) => set('product', e.target.value)} className={input} data-testid="rfq-product" />
                  </div>
                  <div>
                    <label className={label}>{r.f.desc}</label>
                    <textarea value={form.description} placeholder={r.f.descPh} onChange={(e) => set('description', e.target.value)} className={textarea} data-testid="rfq-description" />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className={label}>{r.f.qty}</label>
                      <input type="text" value={form.quantity_value} placeholder={r.f.qtyPh} onChange={(e) => set('quantity_value', e.target.value)} className={input} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={label}>{r.f.unit}</label>
                      <input type="text" value={form.quantity_unit} placeholder={r.f.unitPh} onChange={(e) => set('quantity_unit', e.target.value)} className={input} />
                    </div>
                  </div>
                  <div>
                    <label className={label}>{r.f.freq}</label>
                    <div className="flex flex-wrap gap-2">
                      {FREQUENCIES.map((f) => {
                        const active = form.frequency === f;
                        return (
                          <label key={f} className={`${chip} ${active ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-brand-navy border-gray-300 hover:border-brand-yellow'}`}>
                            <input type="radio" name="frequency" value={f} checked={active} onChange={() => set('frequency', f)} className="sr-only" />
                            {r.freq[f]}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className={label}>{r.f.budget}</label>
                      <input type="text" value={form.budget_amount} placeholder={r.f.budgetPh} onChange={(e) => set('budget_amount', e.target.value)} className={input} />
                    </div>
                    <div>
                      <label className={label}>{r.f.currency}</label>
                      <select value={form.budget_currency} onChange={(e) => set('budget_currency', e.target.value)} className={input}>
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className={label}>{r.f.market}</label>
                      <input type="text" value={form.market} placeholder={r.f.marketPh} onChange={(e) => set('market', e.target.value)} className={input} />
                    </div>
                    <div>
                      <label className={label}>{r.f.timeline}</label>
                      <input type="text" value={form.timeline} placeholder={r.f.timelinePh} onChange={(e) => set('timeline', e.target.value)} className={input} />
                    </div>
                  </div>
                  <div>
                    <label className={label}>{r.f.standards}</label>
                    <input type="text" value={form.standards} placeholder={r.f.standardsPh} onChange={(e) => set('standards', e.target.value)} className={input} />
                  </div>
                  <div>
                    <label className={label}>{r.f.custom}</label>
                    <div className="flex gap-2">
                      {['yes', 'no'].map((v) => {
                        const active = form.customization_required === v;
                        return (
                          <label key={v} className={`${chip} ${active ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-brand-navy border-gray-300 hover:border-brand-yellow'}`}>
                            <input type="radio" name="customization_required" value={v} checked={active} onChange={() => set('customization_required', v)} className="sr-only" />
                            {v === 'yes' ? r.f.customYes : r.f.customNo}
                          </label>
                        );
                      })}
                    </div>
                    {form.customization_required === 'yes' && (
                      <div className="mt-3">
                        <label className={label}>{r.f.customDetails}</label>
                        <textarea value={form.customization_details} placeholder={r.f.customDetailsPh} onChange={(e) => set('customization_details', e.target.value)} className={textarea} />
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Section 3 — Files */}
              <section>
                <h3 className="text-lg font-bold text-brand-navy mb-1">{r.s3}</h3>
                <p className="text-sm text-gray-500 mb-4">{r.s3Sub}</p>
                <div className="border-2 border-dashed border-gray-300 hover:border-brand-yellow rounded-xl p-6 text-center transition">
                  <input ref={fileRef} type="file" multiple accept={ALLOWED_EXT.map((e) => '.' + e).join(',')} onChange={(e) => addFiles(e.target.files)} className="hidden" data-testid="rfq-files-input" />
                  <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy/90 transition">
                    <Paperclip className="w-4 h-4" /> {r.filesLabel}
                  </button>
                  <p className="text-xs text-gray-500 mt-3">{r.filesHint}</p>
                </div>
                {files.length > 0 ? (
                  <ul className="mt-4 space-y-2" data-testid="rfq-files-list">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-4 h-4 text-brand-yellow shrink-0" />
                          <span className="text-sm text-brand-navy truncate">{f.name}</span>
                          <span className="text-xs text-gray-500 shrink-0">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                        <button type="button" onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 transition">
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-400 mt-3">{r.filesEmpty}</p>
                )}
                <div className="mt-5">
                  <label className={label}>{r.refUrl}</label>
                  <input type="url" value={form.reference_url} placeholder={r.refUrlPh} onChange={(e) => set('reference_url', e.target.value)} className={input} />
                </div>
              </section>

              {/* Section 4 — Contact */}
              <section>
                <h3 className="text-lg font-bold text-brand-navy mb-4">{r.s4}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className={label}>{r.c.name}{req}</label>
                    <input type="text" required value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className={input} data-testid="rfq-contact-name" />
                  </div>
                  <div>
                    <label className={label}>{r.c.email}{req}</label>
                    <input type="email" required value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} className={input} data-testid="rfq-contact-email" />
                  </div>
                  <div>
                    <label className={label}>{r.c.company}</label>
                    <input type="text" value={form.contact_company} onChange={(e) => set('contact_company', e.target.value)} className={input} />
                  </div>
                  <div>
                    <label className={label}>{r.c.role}</label>
                    <input type="text" value={form.contact_role} onChange={(e) => set('contact_role', e.target.value)} className={input} />
                  </div>
                  <div>
                    <label className={label}>{r.c.country}</label>
                    <input type="text" value={form.contact_country} onChange={(e) => set('contact_country', e.target.value)} className={input} />
                  </div>
                  <div>
                    <label className={label}>{r.c.phone}</label>
                    <input type="tel" value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} className={input} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={label}>{r.c.website}</label>
                    <input type="url" value={form.contact_website} onChange={(e) => set('contact_website', e.target.value)} className={input} />
                  </div>
                </div>
                {/* Honeypot — hidden from real users */}
                <input type="text" tabIndex={-1} autoComplete="off" value={form.hp} onChange={(e) => set('hp', e.target.value)} className="absolute -left-[9999px] w-0 h-0 opacity-0" aria-hidden="true" />
              </section>
            </div>

            {/* Footer bar */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 md:px-8 py-4 flex flex-col-reverse sm:flex-row items-center sm:justify-between gap-3">
              <div className="text-xs text-gray-500 flex-1 min-w-0">
                {err ? (
                  <span className="text-red-600 font-medium" data-testid="rfq-error">{err}</span>
                ) : RECAPTCHA_SITE_KEY ? (
                  <span className="text-[10px] text-gray-400">
                    Protégé par reCAPTCHA — <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-brand-navy">Confidentialité</a> · <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-brand-navy">Conditions</a>
                  </span>
                ) : null}
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button type="button" onClick={close} className="px-5 py-2.5 rounded-full border border-gray-300 text-brand-navy text-sm font-semibold hover:border-brand-navy transition">
                  {r.cancel}
                </button>
                <button type="submit" disabled={busy} data-testid="rfq-submit-button" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 transition disabled:opacity-60 disabled:cursor-not-allowed">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {busy ? r.submitting : r.submit}
                </button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
